/**
 * SustainabilityDeclarationForm.jsx — Sürdürülebilirlik Beyanı Formu
 * Route: /FdsSaha/Sustainability
 *
 * Tek sayfa, kağıt/belge tarzı görünüm.
 * Tüm bölümler aynı anda görünür, scroll ile geçilir.
 */

import React, { useState } from "react";
import {
  Button, Input, Label, FormGroup, Alert, Spinner,
} from "reactstrap";
import { Leaf, CheckCircle2, Send } from "lucide-react";
import { PageFrame, PageBody } from "../../Components/Common/EmbeddablePage";
import TarihGirdisi from "../../Components/Common/TarihGirdisi";
import { submitSustainabilityForm } from "../../services/sustainabilityService";
import { useTranslation } from "react-i18next";

// ─── Renk Sabiti ─────────────────────────────────────────────────────────────
const NAVY = "#0d2060";

// ─── Başlangıç Verisi ─────────────────────────────────────────────────────────
const INITIAL = {
  exhibitorName: "",
  standNumber: "",
  standContractor: "",
  standSize: "",
  fuarId: localStorage.getItem("activeFairId") || "",
  woodPct: "",
  aluminiumPct: "",
  steelPct: "",
  otherPct: "",
  fscWood: "",
  recycledMaterials: "",
  recycledDetails: "",
  graphicsType: [],
  graphicsReuse: "",
  flooringType: "",
  flooringReusable: "",
  standReusability: "",
  prevUsed: "",
  futureReuse: "",
  reusePct: "",
  ledLighting: "",
  digitalDisplays: "",
  rentalFurniture: "",
  manufacturingCountry: "",
  materialsOrigin: "",
  localContractor: "",
  localSuppliers: "",
  endOfLife: [],
  additionalInitiatives: "",
  declarantName: "",
  declarantCompany: "",
  declarantDate: new Date().toISOString().slice(0, 10),
  declarationConfirmed: false,
};

// ─── Kağıt Bileşenleri ────────────────────────────────────────────────────────

function SectionHeader({ title }) {
  return (
    <div style={{ background: NAVY, color: "#fff", textAlign: "center", fontWeight: "700", fontSize: "0.95rem", padding: "10px 16px", letterSpacing: "0.4px" }}>
      {title}
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div style={{ display: "flex", borderBottom: "1px solid #cdd6e0", background: "#eef2f7", minHeight: 38 }}>
      <div style={{ width: 300, flexShrink: 0, padding: "6px 12px", color: NAVY, fontWeight: "600", fontSize: "0.82rem", borderRight: "1px solid #cdd6e0", display: "flex", alignItems: "center" }}>
        {label}
      </div>
      <div style={{ flex: 1, padding: "4px 10px", display: "flex", alignItems: "center" }}>
        {children}
      </div>
    </div>
  );
}

function FieldRow2({ left, right }) {
  return (
    <div style={{ display: "flex", borderBottom: "1px solid #cdd6e0", background: "#eef2f7", minHeight: 38 }}>
      <div style={{ flex: 1, display: "flex", borderRight: "1px solid #cdd6e0" }}>
        <div style={{ width: 180, flexShrink: 0, padding: "6px 12px", color: NAVY, fontWeight: "600", fontSize: "0.82rem", borderRight: "1px solid #cdd6e0", display: "flex", alignItems: "center" }}>{left.label}</div>
        <div style={{ flex: 1, padding: "4px 8px", display: "flex", alignItems: "center" }}>{left.field}</div>
      </div>
      <div style={{ flex: 1, display: "flex" }}>
        <div style={{ width: 180, flexShrink: 0, padding: "6px 12px", color: NAVY, fontWeight: "600", fontSize: "0.82rem", borderRight: "1px solid #cdd6e0", display: "flex", alignItems: "center" }}>{right.label}</div>
        <div style={{ flex: 1, padding: "4px 8px", display: "flex", alignItems: "center" }}>{right.field}</div>
      </div>
    </div>
  );
}

function TF({ value, onChange, placeholder = "", type = "text", min, max }) {
  return (
    <Input bsSize="sm" type={type} value={value} onChange={onChange} placeholder={placeholder} min={min} max={max}
      style={{ border: "1px solid #b0bec5", borderRadius: 3, fontSize: "0.82rem" }} />
  );
}

function YN({ id, value, onChange }) {
  const { t } = useTranslation("saha");
  return (
    <div className="d-flex gap-3">
      {[["yes", t("common:evet")], ["no", t("common:hayir")]].map(([v, lbl]) => (
        <Label check key={v} className="d-flex align-items-center gap-1 mb-0" style={{ cursor: "pointer", fontSize: "0.82rem" }}>
          <Input type="radio" name={id} value={v} checked={value === v} onChange={() => onChange(v)} /> {lbl}
        </Label>
      ))}
    </div>
  );
}

function Multi({ options, value = [], onChange }) {
  const toggle = (opt) => onChange(value.includes(opt) ? value.filter((x) => x !== opt) : [...value, opt]);
  return (
    <div className="d-flex flex-wrap gap-3">
      {options.map((opt) => (
        <Label check key={opt} className="d-flex align-items-center gap-1 mb-0" style={{ cursor: "pointer", fontSize: "0.82rem" }}>
          <Input type="checkbox" checked={value.includes(opt)} onChange={() => toggle(opt)} /> {opt}
        </Label>
      ))}
    </div>
  );
}

// ─── Ana Bileşen ──────────────────────────────────────────────────────────────
// embedded: boolean true → sayfa kabuğu kapanır (sekme olarak render edilir)
export default function SustainabilityDeclarationForm({ embedded = false }) {
  const { t } = useTranslation("saha");
  const [data,         setData]         = useState(INITIAL);
  const [submitting,   setSubmitting]   = useState(false);
  const [done,         setDone]         = useState(false);
  const [error,        setError]        = useState(null);
  const [submissionId, setSubmissionId] = useState(null);

  const set = (key, val) => setData((prev) => ({ ...prev, [key]: val }));

  function validate() {
    if (!data.exhibitorName.trim())    return "Exhibitor Name zorunludur.";
    if (!data.standNumber.trim())      return "Stand Number zorunludur.";
    if (!data.standSize)               return "Stand Size zorunludur.";
    if (!data.declarantName.trim())    return "Declaration: Name & Surname zorunludur.";
    if (!data.declarantCompany.trim()) return "Declaration: Company zorunludur.";
    if (!data.declarationConfirmed)    return t("sustainabilityDeclarationForm.beyaniOnaylamanizGerekmektedir");
    return null;
  }

  async function handleSubmit() {
    const err = validate();
    if (err) { setError(err); window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    setError(null);
    setSubmitting(true);
    try {
      const result = await submitSustainabilityForm({ ...data, fuarId: localStorage.getItem("activeFairId") || null });
      setSubmissionId(result?.id ?? null);
      setDone(true);
    } catch (e) {
      setError(t("sustainabilityDeclarationForm.formGonderilirkenHataOlustu") + (e?.message || t("sustainabilityDeclarationForm.bilinmeyenHata")));
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <PageFrame embedded={embedded}>
        <PageBody embedded={embedded} title={t("sustainabilityDeclarationForm.surdurulebilirlikBeyani")} pageTitle={t("sustainabilityDeclarationForm.fdsSaha")}>
          <div className="text-center py-5">
            <CheckCircle2 size={64} className="text-success mb-3" />
            <h4 className="text-success fw-bold">{t("sustainabilityDeclarationForm.formBasariylaGonderildi")}</h4>
            <p className="text-muted">{t("sustainabilityDeclarationForm.surdurulebilirlikBeyaniKaydedildi")}</p>
            {submissionId && <p className="text-muted small">{t("sustainabilityDeclarationForm.kayitNo")}{" "}<code>{submissionId}</code></p>}
            <Button color="outline-secondary" className="mt-2" onClick={() => { setData(INITIAL); setDone(false); setError(null); setSubmissionId(null); }}>
              {t("sustainabilityDeclarationForm.yeniFormDoldur")}
            </Button>
          </div>
        </PageBody>
      </PageFrame>
    );
  }

  return (
    <PageFrame embedded={embedded}>
      <PageBody embedded={embedded} title={t("sustainabilityDeclarationForm.surdurulebilirlikBeyaniFormu")} pageTitle={t("sustainabilityDeclarationForm.fdsSaha")}>
        <div style={{ maxWidth: 980 }}>

        {/* Başlık */}
        <div className="mb-4 rounded-top" style={{ background: NAVY, color: "#fff", padding: "16px 24px", display: "flex", alignItems: "center", gap: 14 }}>
          <Leaf size={28} />
          <div>
            <div style={{ fontWeight: 700, fontSize: "1.1rem", letterSpacing: 1 }}>SUSTAINABILITY DECLARATION FORM</div>
            <div style={{ fontSize: "0.8rem", opacity: 0.75 }}>{t("sustainabilityDeclarationForm.standSurdurulebilirlikBeyaniSakaGroup")}</div>
          </div>
        </div>

        {error && <Alert color="danger" toggle={() => setError(null)} className="mb-3">{error}</Alert>}

        {/* Kağıt Form */}
        <div className="mb-4" style={{ border: "1px solid #cdd6e0", borderRadius: 4, overflow: "hidden", background: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>

          {/* 1. General Information */}
          <SectionHeader title="General Information" />
          <FieldRow2
            left={{ label: "Exhibitor Name :", field: <TF value={data.exhibitorName} onChange={e => set("exhibitorName", e.target.value)} placeholder={t("sustainabilityDeclarationForm.firmaAdi")} /> }}
            right={{ label: "Stand Number :", field: <TF value={data.standNumber} onChange={e => set("standNumber", e.target.value)} placeholder={t("sustainabilityDeclarationForm.orA12")} /> }}
          />
          <FieldRow2
            left={{ label: "Stand Contractor :", field: <TF value={data.standContractor} onChange={e => set("standContractor", e.target.value)} placeholder={t("sustainabilityDeclarationForm.dekoratorMuteahhit")} /> }}
            right={{ label: "Stand Size (sqm) :", field: <TF type="number" min={0} value={data.standSize} onChange={e => set("standSize", e.target.value)} placeholder="m²" /> }}
          />

          {/* 2. Materials */}
          <SectionHeader title="Materials" />
          <FieldRow label="Percentage of stand structure made from wood :">
            <TF type="number" min={0} max={100} value={data.woodPct} onChange={e => set("woodPct", e.target.value)} placeholder="%" />
          </FieldRow>
          <FieldRow label="Percentage of stand structure made from aluminium :">
            <TF type="number" min={0} max={100} value={data.aluminiumPct} onChange={e => set("aluminiumPct", e.target.value)} placeholder="%" />
          </FieldRow>
          <FieldRow label="Percentage of stand structure made from steel :">
            <TF type="number" min={0} max={100} value={data.steelPct} onChange={e => set("steelPct", e.target.value)} placeholder="%" />
          </FieldRow>
          <FieldRow label="Percentage of other materials :">
            <TF type="number" min={0} max={100} value={data.otherPct} onChange={e => set("otherPct", e.target.value)} placeholder="%" />
          </FieldRow>
          <FieldRow label="Is FSC-certified or sustainably sourced wood used? :">
            <YN id="fscWood" value={data.fscWood} onChange={v => set("fscWood", v)} />
          </FieldRow>
          <FieldRow label="Are recycled materials incorporated into the stand? :">
            <YN id="recycledMaterials" value={data.recycledMaterials} onChange={v => set("recycledMaterials", v)} />
          </FieldRow>
          <FieldRow label="If yes, please specify materials and approximate percentage :">
            <TF value={data.recycledDetails} onChange={e => set("recycledDetails", e.target.value)} placeholder={t("sustainabilityDeclarationForm.or30GeriDonusturulmusAluminyum")} />
          </FieldRow>

          {/* 3. Graphics & Branding */}
          <SectionHeader title="Graphics & Branding" />
          <FieldRow label="Type of graphics used :">
            <TF value={typeof data.graphicsType === "string" ? data.graphicsType : (data.graphicsType || []).join(", ")}
              onChange={e => set("graphicsType", e.target.value)} placeholder={t("sustainabilityDeclarationForm.orPvcKumasDijital")} />
          </FieldRow>
          <FieldRow label="PVC graphics :">
            <YN id="pvcGraphics" value={data.pvcGraphics || ""} onChange={v => set("pvcGraphics", v)} />
          </FieldRow>
          <FieldRow label="PVC-free graphics :">
            <YN id="pvcFreeGraphics" value={data.pvcFreeGraphics || ""} onChange={v => set("pvcFreeGraphics", v)} />
          </FieldRow>
          <FieldRow label="Fabric graphics :">
            <YN id="fabricGraphics" value={data.fabricGraphics || ""} onChange={v => set("fabricGraphics", v)} />
          </FieldRow>
          <FieldRow label="Recyclable graphics :">
            <YN id="recyclableGraphics" value={data.recyclableGraphics || ""} onChange={v => set("recyclableGraphics", v)} />
          </FieldRow>
          <FieldRow label="Are graphics intended for reuse after the event? :">
            <YN id="graphicsReuse" value={data.graphicsReuse} onChange={v => set("graphicsReuse", v)} />
          </FieldRow>

          {/* 4. Flooring */}
          <SectionHeader title="Flooring" />
          <FieldRow label="Type of flooring used :">
            <TF value={data.flooringType} onChange={e => set("flooringType", e.target.value)} placeholder={t("sustainabilityDeclarationForm.orAhsapHaliVinilEpoksi")} />
          </FieldRow>
          <FieldRow label="Is the flooring reusable? :">
            <YN id="flooringReusable" value={data.flooringReusable} onChange={v => set("flooringReusable", v)} />
          </FieldRow>

          {/* 5. Reusability */}
          <SectionHeader title="Reusability" />
          <FieldRow label="Stand structure type :">
            <div className="d-flex flex-column gap-1">
              {[["single", "Single-use custom build"], ["partial", "Partially reusable"], ["full", "Fully modular and reusable"]].map(([v, lbl]) => (
                <Label check key={v} className="d-flex align-items-center gap-2 mb-0" style={{ cursor: "pointer", fontSize: "0.82rem" }}>
                  <Input type="radio" name="standReusability" value={v} checked={data.standReusability === v} onChange={() => set("standReusability", v)} /> {lbl}
                </Label>
              ))}
            </div>
          </FieldRow>
          <FieldRow label="Have stand elements been used at previous events? :">
            <YN id="prevUsed" value={data.prevUsed} onChange={v => set("prevUsed", v)} />
          </FieldRow>
          <FieldRow label="Will stand elements be reused at future events? :">
            <YN id="futureReuse" value={data.futureReuse} onChange={v => set("futureReuse", v)} />
          </FieldRow>
          <FieldRow label="Percentage of stand elements planned for reuse (%) :">
            <TF type="number" min={0} max={100} value={data.reusePct} onChange={e => set("reusePct", e.target.value)} placeholder="%" />
          </FieldRow>

          {/* 6. Sustainable Operations */}
          <SectionHeader title="Sustainable Operations" />
          <FieldRow label="Is LED lighting used throughout the stand? :">
            <YN id="ledLighting" value={data.ledLighting} onChange={v => set("ledLighting", v)} />
          </FieldRow>
          <FieldRow label="Are digital displays used to reduce printed materials? :">
            <YN id="digitalDisplays" value={data.digitalDisplays} onChange={v => set("digitalDisplays", v)} />
          </FieldRow>
          <FieldRow label="Is rental or reusable furniture used? :">
            <YN id="rentalFurniture" value={data.rentalFurniture} onChange={v => set("rentalFurniture", v)} />
          </FieldRow>

          {/* 7. Logistics & Supply Chain */}
          <SectionHeader title="Logistics & Supply Chain" />
          <FieldRow2
            left={{ label: "Country of stand manufacture :", field: <TF value={data.manufacturingCountry} onChange={e => set("manufacturingCountry", e.target.value)} placeholder={t("sustainabilityDeclarationForm.ulke")} /> }}
            right={{ label: "Country of origin of materials :", field: <TF value={data.materialsOrigin} onChange={e => set("materialsOrigin", e.target.value)} placeholder={t("sustainabilityDeclarationForm.ulke")} /> }}
          />
          <FieldRow label="Is the stand contractor based locally (Turkey)? :">
            <YN id="localContractor" value={data.localContractor} onChange={v => set("localContractor", v)} />
          </FieldRow>
          <FieldRow label="Are local suppliers used? :">
            <YN id="localSuppliers" value={data.localSuppliers} onChange={v => set("localSuppliers", v)} />
          </FieldRow>

          {/* 8. End-of-Life */}
          <SectionHeader title="End-of-Life Commitment" />
          <FieldRow label="Planned destination of stand materials after the event :">
            <Multi options={["Reuse", "Storage for next event", "Donation", "Recycling", "Disposal"]} value={data.endOfLife} onChange={v => set("endOfLife", v)} />
          </FieldRow>

          {/* 9. Additional */}
          <SectionHeader title="Additional Sustainability Initiatives" />
          <div style={{ background: "#eef2f7", padding: "10px 12px", borderBottom: "1px solid #cdd6e0" }}>
            <Input type="textarea" rows={4} bsSize="sm" value={data.additionalInitiatives}
              onChange={e => set("additionalInitiatives", e.target.value)}
              placeholder="Please describe any other sustainability measures applied to your stand design…"
              style={{ fontSize: "0.82rem", border: "1px solid #b0bec5" }} />
          </div>

          {/* 10. Declaration */}
          <SectionHeader title="Declaration" />
          <div style={{ background: "#eef2f7", padding: "14px 16px" }}>
            <p className="mb-3 fst-italic small text-muted">
              "I confirm that the information provided is accurate and can be verified by SAKA Group during on-site inspections."
            </p>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 200px" }}>
                <div style={{ color: NAVY, fontWeight: 600, fontSize: "0.82rem", marginBottom: 4 }}>Name & Surname *</div>
                <TF value={data.declarantName} onChange={e => set("declarantName", e.target.value)} placeholder={t("sustainabilityDeclarationForm.yetkiliAdi")} />
              </div>
              <div style={{ flex: "1 1 200px" }}>
                <div style={{ color: NAVY, fontWeight: 600, fontSize: "0.82rem", marginBottom: 4 }}>Company *</div>
                <TF value={data.declarantCompany} onChange={e => set("declarantCompany", e.target.value)} placeholder={t("sustainabilityDeclarationForm.sirketAdi")} />
              </div>
              <div style={{ flex: "0 0 160px" }}>
                <div style={{ color: NAVY, fontWeight: 600, fontSize: "0.82rem", marginBottom: 4 }}>Date *</div>
                <TarihGirdisi
                  value={data.declarantDate} onChange={v => set("declarantDate", v)}
                  inputStyle={{ border: "1px solid #b0bec5", borderRadius: "3px", fontSize: "0.82rem" }}
                />
              </div>
            </div>
            <FormGroup check className="mt-3 mb-0">
              <Input type="checkbox" id="declarationConfirmed" checked={data.declarationConfirmed}
                onChange={e => set("declarationConfirmed", e.target.checked)} />
              <Label check for="declarationConfirmed" style={{ fontWeight: 600, fontSize: "0.85rem", color: NAVY }}>
                I have read and confirm the above declaration. <span className="text-danger">*</span>
              </Label>
            </FormGroup>
          </div>

        </div>{/* /kağıt form */}

        {/* Gönder */}
        <div className="d-flex justify-content-end mb-5">
          <Button color="success" size="lg" disabled={submitting} onClick={handleSubmit}
            className="d-flex align-items-center gap-2 px-5">
            {submitting ? <Spinner size="sm" /> : <Send size={16} />}
            {submitting ? t("sustainabilityDeclarationForm.gonderiliyor") : "Submit Form"}
          </Button>
        </div>

        </div>
      </PageBody>
    </PageFrame>
  );
}
